import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, catchError, interval, Observable, of, Subscription, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { AnnouncementModel } from '../../core/models/announcement.model';

@Injectable({
  providedIn: 'root'
})
export class AnnouncementService {
  private apiUrl = environment.apiUrl + '/loan/api/admin';
  private currentAnnouncementSubject = new BehaviorSubject<AnnouncementModel | null>(null);
  public currentAnnouncement$ = this.currentAnnouncementSubject.asObservable();

  private pollingInterval = 15000; // 15 seconds
  private pollingSubscription?: Subscription;

  constructor(private http: HttpClient) {
    this.startPolling();
  }

  // CRUD Operations
  findAll(): Observable<AnnouncementModel[]> {
    return this.http.get<AnnouncementModel[]>(`${this.apiUrl}/findAllAnnouncements`);
  }

  findActive(): Observable<AnnouncementModel | null> {
    return this.http.get<AnnouncementModel | null>(`${this.apiUrl}/findActiveAnnouncement`);
  }

  save(announcement: Partial<AnnouncementModel>): Observable<AnnouncementModel> {
    return this.http.post<AnnouncementModel>(`${this.apiUrl}/saveAnnouncement`, announcement);
  }

  update(id: number, announcement: Partial<AnnouncementModel>): Observable<AnnouncementModel> {
    return this.http.post<AnnouncementModel>(`${this.apiUrl}/updateAnnouncement/${id}`, announcement);
  }

  sendAnnouncement(id: number, username: string): Observable<AnnouncementModel> {
    return this.http.post<AnnouncementModel>(`${this.apiUrl}/sendAnnouncement/${id}`, { username });
  }

  clearAnnouncement(id: number, username: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/clearAnnouncement/${id}`, { username });
  }

  delete(id: number, username: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/deleteAnnouncement/${id}`, {
      body: { username }
    });
  }

  getAuditTrail(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getAnnouncementAuditTrail`);
  }

  getAuditTrailByAnnouncementId(id: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getAnnouncementAuditTrail/${id}`);
  }

  private startPolling(): void {
    // Initial fetch
    this.fetchActiveAnnouncement();

    // Avoid creating duplicate intervals when service is re-instantiated in tests.
    this.pollingSubscription?.unsubscribe();

    // Poll every 15 seconds
    this.pollingSubscription = interval(this.pollingInterval).pipe(
      switchMap(() => this.fetchActiveAnnouncement()),
      catchError(error => {
        console.error('Error polling announcements:', error);
        return of(null);
      })
    ).subscribe();
  }

  private fetchActiveAnnouncement(): Observable<AnnouncementModel | null> {
    return this.findActive().pipe(
      map(announcement => {
        this.currentAnnouncementSubject.next(announcement);
        return announcement;
      })
    );
  }

  refresh(): void {
    this.fetchActiveAnnouncement().subscribe();
  }

  ngOnDestroy(): void {
    this.pollingSubscription?.unsubscribe();
  }
}
